import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Typography,
  TablePagination,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Collapse,
  Avatar,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HomeIcon from '@mui/icons-material/Home';
import { contactService, getPhotoUrl } from '../../services/api';
import ContactList from '../Contact/ContactList';
import ContactForm from '../Contact/ContactForm';

const PessoaList = ({ pessoas, onEdit, onDelete, onToggleActive }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);
  const [contacts, setContacts] = useState({});
  const [openContactForm, setOpenContactForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const currentUser = JSON.parse(localStorage.getItem('user'));

  // Se não houver usuário logado, redireciona para login
  if (!currentUser) {
    window.location.href = '/login';
    return null;
  }

  const canEditUser = (pessoa) => {
    if (!pessoa) return false;

    // Qualquer usuário pode editar seu próprio perfil
    if (currentUser.id === pessoa.id) {
      return true;
    }

    // Administrador pode editar qualquer usuário
    if (currentUser.profile === 'Administrador') {
      return true;
    }

    // Gestor pode editar usuários dos seus lares e com perfis específicos
    if (currentUser.profile === 'Gestor') {
      const gestorLarIds = currentUser.laresVinculados?.map(lar => lar.id) || [];
      const pessoaLarIds = pessoa.laresVinculados?.map(lar => lar.id) || [];
      const hasCommonLar = gestorLarIds.some(id => pessoaLarIds.includes(id));
      const hasEditableProfile = ['AuxiliarEnfermagem', 'Cuidador', 'Enfermeiro', 'Familiar', 'Medico', 'Residente', 'TecnicoEnfermagem', 'Outro'].includes(pessoa.profile);
      return hasCommonLar && hasEditableProfile;
    }

    // Outros perfis podem editar residentes do mesmo lar
    if (['AuxiliarEnfermagem', 'Cuidador', 'Enfermeiro', 'Familiar', 'Medico', 'TecnicoEnfermagem', 'Outro'].includes(currentUser.profile)) {
      const currentUserLarIds = currentUser.laresVinculados?.map(lar => lar.id) || [];
      const pessoaLarIds = pessoa.laresVinculados?.map(lar => lar.id) || [];
      const hasCommonLar = currentUserLarIds.some(id => pessoaLarIds.includes(id));

      // Pode editar residentes do mesmo lar
      if (hasCommonLar && pessoa.profile === 'Residente') {
        return true;
      }
    }

    return false;
  };

  const canViewUser = (pessoa) => {
    if (!pessoa) return false;
    
    // Não pode ver administradores
    if (pessoa.profile === 'Administrador') {
      return false;
    }

    // Administrador pode ver todos
    if (currentUser.profile === 'Administrador') {
      return true;
    }

    // Gestor pode ver usuários dos seus lares
    if (currentUser.profile === 'Gestor') {
      const gestorLarIds = currentUser.laresVinculados?.map(lar => lar.id) || [];
      const pessoaLarIds = pessoa.laresVinculados?.map(lar => lar.id) || [];
      return gestorLarIds.some(id => pessoaLarIds.includes(id));
    }

    // Outros perfis podem ver usuários do mesmo lar
    const currentUserLarIds = currentUser.laresVinculados?.map(lar => lar.id) || [];
    const pessoaLarIds = pessoa.laresVinculados?.map(lar => lar.id) || [];
    return currentUserLarIds.some(id => pessoaLarIds.includes(id));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    return phone.replace(/^(\+55)(\d{2})(\d{5})(\d{4})$/, '+55 ($2) $3-$4');
  };

  const getProfileColor = (profile) => {
    const colors = {
      Administrador: 'error',
      Gestor: 'warning',
      Medico: 'info',
      Enfermeiro: 'success',
      TecnicoEnfermagem: 'secondary',
      AuxiliarEnfermagem: 'default',
      Cuidador: 'primary',
      Familiar: 'info',
      Residente: 'warning',
      Outro: 'default'
    };
    return colors[profile] || 'default';
  };

  const handleExpandRow = async (userId) => {
    if (expandedRow === userId) {
      setExpandedRow(null);
      return;
    }

    setExpandedRow(userId);
    if (!contacts[userId]) {
      try {
        const response = await contactService.getByUserId(userId);
        setContacts(prev => ({ ...prev, [userId]: response.data }));
      } catch (error) {
        console.error('Erro ao carregar contatos:', error);
      }
    }
  };

  const handleAddContact = (userId) => {
    setSelectedUserId(userId);
    setSelectedContact(null);
    setOpenContactForm(true);
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setSelectedUserId(expandedRow);
    setOpenContactForm(true);
  };

  const handleDeleteContact = (contact) => {
    setConfirmDelete(contact);
  };

  const handleConfirmDeleteContact = async () => {
    try {
      await contactService.delete(expandedRow, confirmDelete.id);
      
      // Atualizar a lista de contatos
      const response = await contactService.getByUserId(expandedRow);
      setContacts(prev => ({ ...prev, [expandedRow]: response.data }));
      
      setConfirmDelete(null);
    } catch (error) {
      console.error('Erro ao excluir contato:', error);
    }
  };

  const handleContactSubmit = async (formData) => {
    try {
      if (selectedContact) {
        // Editar contato existente
        await contactService.update(selectedUserId, selectedContact.id, formData);
      } else {
        // Criar novo contato
        await contactService.create(selectedUserId, formData);
      }

      // Atualizar a lista de contatos
      const response = await contactService.getByUserId(selectedUserId);
      setContacts(prev => ({ ...prev, [selectedUserId]: response.data }));

      setOpenContactForm(false);
      setSelectedContact(null);
      setSelectedUserId(null);
    } catch (error) {
      console.error('Erro ao salvar contato:', error);
    }
  };

  const calculateAge = (birthday) => {
    if (!birthday) return '-';
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return `${age} anos`;
  };

  const isAnniversary = (birthday) => {
    if (!birthday) return false;
    const today = new Date();
    const birthDate = new Date(birthday);
    return today.getMonth() === birthDate.getMonth() && 
           today.getDate() === birthDate.getDate();
  };

  const handlePersonClick = (pessoa) => {
    if (canEditUser(pessoa)) {
      onEdit({ ...pessoa, mode: 'edit' });
    } else if (canViewUser(pessoa)) {
      onEdit({ ...pessoa, mode: 'view' });
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Contatos</TableCell>
              <TableCell>Foto</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>Perfil</TableCell>
              <TableCell>Idade</TableCell>
              <TableCell>Lar(es)</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? pessoas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : pessoas
            ).map((pessoa) => (
              <React.Fragment key={pessoa.id}>
                <TableRow>
                  <TableCell>
                    <Tooltip title={expandedRow === pessoa.id ? "Ocultar contatos" : "Mostrar contatos"}>
                      <IconButton
                        size="small"
                        onClick={() => handleExpandRow(pessoa.id)}
                      >
                        {expandedRow === pessoa.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box 
                      sx={{ 
                        cursor: canViewUser(pessoa) ? 'pointer' : 'default',
                        '&:hover': canViewUser(pessoa) ? {
                          '& .MuiAvatar-root': {
                            opacity: 0.8
                          }
                        } : {}
                      }}
                      onClick={() => canViewUser(pessoa) && handlePersonClick(pessoa)}
                    >
                      <Avatar 
                        src={getPhotoUrl(pessoa.photo)} 
                        alt={pessoa.name}
                        sx={{
                          transition: 'opacity 0.2s'
                        }}
                      >
                        {pessoa.name.charAt(0)}
                      </Avatar>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography 
                          variant="body1"
                          sx={{
                            transition: 'color 0.2s'
                          }}
                        >
                          {pessoa.name}
                        </Typography>
                        {pessoa.nickname && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              fontSize: '0.85em',
                              transition: 'color 0.2s'
                            }}
                          >
                            ({pessoa.nickname})
                          </Typography>
                        )}
                      </Box>
                      {contacts[pessoa.id]?.length > 0 && (
                        <Tooltip title="Clique na seta à esquerda para ver os contatos">
                          <Chip 
                            size="small" 
                            color="info" 
                            label={`${contacts[pessoa.id].length} contato${contacts[pessoa.id].length > 1 ? 's' : ''}`}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{pessoa.email}</TableCell>
                  <TableCell>{formatPhone(pessoa.phone1)}</TableCell>
                  <TableCell>
                    <Chip
                      label={pessoa.profile}
                      color={getProfileColor(pessoa.profile)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: isAnniversary(pessoa.birthday) ? 'bold' : 'normal' 
                        }}
                      >
                        {pessoa.birthday ? calculateAge(pessoa.birthday) : '-'}
                      </Typography>
                      {isAnniversary(pessoa.birthday) && (
                        <Tooltip title="Aniversário hoje!">
                          <VisibilityIcon color="primary" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {pessoa.laresVinculados && pessoa.laresVinculados.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {pessoa.laresVinculados.map((lar) => (
                          <Tooltip key={lar.id} title={lar.nome} arrow placement="top">
                            <HomeIcon 
                              color="primary" 
                              fontSize="small" 
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': {
                                  color: 'secondary.main',
                                  transform: 'scale(1.2)',
                                  transition: 'all 0.2s'
                                }
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Não definido
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {canViewUser(pessoa) && (
                        <Tooltip title="Visualizar">
                          <IconButton
                            size="small"
                            onClick={() => onEdit({ ...pessoa, mode: 'view' })}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEditUser(pessoa) && (
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => onEdit({ ...pessoa, mode: 'edit' })}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={expandedRow === pessoa.id} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Contatos
                            </Typography>
                            {contacts[pessoa.id]?.length > 0 && (
                              <Typography variant="body2" color="text.secondary">
                                ({contacts[pessoa.id].length} {contacts[pessoa.id].length === 1 ? 'contato cadastrado' : 'contatos cadastrados'})
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Adicionar novo contato">
                              <Button
                                startIcon={<AddIcon />}
                                onClick={() => handleAddContact(pessoa.id)}
                                variant="contained"
                                color="primary"
                                size="small"
                              >
                                Adicionar Contato
                              </Button>
                            </Tooltip>
                          </Box>
                        </Box>
                        {contacts[pessoa.id] && contacts[pessoa.id].length > 0 ? (
                          <ContactList
                            contacts={contacts[pessoa.id]}
                            onEdit={handleEditContact}
                            onDelete={handleDeleteContact}
                          />
                        ) : (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              gap: 2,
                              py: 4,
                              bgcolor: 'background.paper',
                              borderRadius: 1
                            }}
                          >
                            <Typography variant="body1" color="text.secondary">
                              Nenhum contato cadastrado
                            </Typography>
                            <Button
                              startIcon={<AddIcon />}
                              onClick={() => handleAddContact(pessoa.id)}
                              variant="outlined"
                              color="primary"
                              size="small"
                            >
                              Cadastrar Primeiro Contato
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={pessoas.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o contato {confirmDelete?.name}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button onClick={handleConfirmDeleteContact} color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <ContactForm
        open={openContactForm}
        onClose={() => {
          setOpenContactForm(false);
          setSelectedContact(null);
          setSelectedUserId(null);
        }}
        onSubmit={handleContactSubmit}
        contact={selectedContact}
        mode={selectedContact ? 'edit' : 'create'}
      />
    </>
  );
};

export default PessoaList;
